"use client"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { FlowListItem } from "@/features/flow/components/flow-list-item"
import { FlowStatus } from "@/interfaces/flow"
import { listFlowsQuery } from "@/queries/flow.query"
import { FlowService } from "@/services/flow.service"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { Link, useNavigate } from "@tanstack/react-router"
import { AxiosError } from "axios"
import { PlusCircle, Search } from "lucide-react"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"

type Flow = {
  id: number;
  name: string;
  description: string;
  status: FlowStatus;
  createdAt: string;
  updatedAt: string;
  lastRun?: string;
  executionCount: number;
  messageCount: number;
  conversionRate?: number;
}

const mockFlows: Flow[] = [
  {
    id: 1,
    name: "Atendimento Inicial",
    description: "Fluxo de atendimento inicial para novos clientes",
    status: FlowStatus.ACTIVE,
    createdAt: "2023-10-01",
    updatedAt: "2023-10-15",
    lastRun: "2023-10-20",
    executionCount: 128,
    messageCount: 1024,
    conversionRate: 68,
  },
  {
    id: 2,
    name: "Suporte Técnico",
    description: "Atendimento para problemas técnicos",
    status: FlowStatus.PAUSED,
    createdAt: "2023-09-15",
    updatedAt: "2023-10-10",
    lastRun: "2023-10-18",
    executionCount: 85,
    messageCount: 742,
    conversionRate: 45,
  },
  {
    id: 3,
    name: "Pesquisa de Satisfação",
    description: "Pesquisa enviada após finalização de atendimento",
    status: FlowStatus.DRAFT,
    createdAt: "2023-10-05",
    updatedAt: "2023-10-05",
    executionCount: 0,
    messageCount: 0,
  },
  {
    id: 4,
    name: "Recuperação de Carrinho Abandonado",
    description: "Fluxo para recuperar clientes que abandonaram o carrinho de compras",
    status: FlowStatus.ERROR,
    createdAt: "2023-09-20",
    updatedAt: "2023-10-12",
    lastRun: "2023-10-19",
    executionCount: 42,
    messageCount: 126,
    conversionRate: 12,
  },
]

const createFlowSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
})

export function FlowsList() {
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const { data: flows } = useSuspenseQuery(listFlowsQuery)
  const { handleSubmit, register } = useForm({ resolver: zodResolver(createFlowSchema) })
  const queryClient = useQueryClient();
  const navigate = useNavigate()

  const handleFormSubmit = handleSubmit((data) => {
    FlowService.create(data)
      .then((flow) => navigate({ to: `/flows/${flow.id}/edit` }))
      .catch((err) => {
        if (err instanceof AxiosError) {
          // TODO: tratar os erros da api
        }
      })
      .finally(() => queryClient.invalidateQueries(listFlowsQuery))
  })

  const filteredFlows = flows.filter((flow) => {
    const matchesSearch =
      flow.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      flow.description.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus = statusFilter === "all" || flow.status === statusFilter

    return matchesSearch && matchesStatus
  })

  const activeFlows = flows.filter((flow) => flow.status === FlowStatus.ACTIVE).length
  const pausedFlows = flows.filter((flow) => flow.status === FlowStatus.PAUSED).length
  const draftFlows = flows.filter((flow) => flow.status === FlowStatus.DRAFT).length

  const handleDeleteFlow = async (id: number) => {
    FlowService.delete(id)
      .then(() => toast.success(`flow deletado`))
      .catch(() => toast.error('Ocorreu um erro ao tentar deletar o flow selecionado'))
      .finally(() => queryClient.invalidateQueries(listFlowsQuery))
  }

  return (
    <div className="container py-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Fluxos de Automação</h1>
        <Button asChild>
          <Link to="/flows">
            <PlusCircle className="w-4 h-4 mr-2" />
            Novo Fluxo
          </Link>
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Fluxos Ativos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{activeFlows}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Fluxos Pausados</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{pausedFlows}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Rascunhos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{draftFlows}</div>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col md:flex-row gap-4 items-end md:items-center">
        <div className="relative w-full md:w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
          <Input
            type="search"
            placeholder="Buscar fluxos..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant={statusFilter === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => setStatusFilter("all")}
          >
            Todos
          </Button>
          <Button
            variant={statusFilter === "active" ? "default" : "outline"}
            size="sm"
            onClick={() => setStatusFilter("active")}
          >
            Ativos
          </Button>
          <Button
            variant={statusFilter === "paused" ? "default" : "outline"}
            size="sm"
            onClick={() => setStatusFilter("paused")}
          >
            Pausados
          </Button>
          <Button
            variant={statusFilter === "draft" ? "default" : "outline"}
            size="sm"
            onClick={() => setStatusFilter("draft")}
          >
            Rascunhos
          </Button>
        </div>
      </div>

      {flows.length === 0 ? (
        <div className="text-center py-12 border rounded-lg bg-gray-50 dark:bg-background">
          <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-4">
            <PlusCircle className="w-6 h-6 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium mb-1">Nenhum fluxo encontrado</h3>
          <p className="text-sm text-gray-500 mb-4">Comece criando seu primeiro fluxo de automação</p>
          <Dialog>
            <DialogTrigger asChild>
              <Button>
                <PlusCircle className="w-4 h-4 mr-2" />
                Criar Fluxo
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Criar Novo Fluxo</DialogTitle>
                <DialogDescription>
                  Preencha os campos abaixo para criar um novo fluxo de automação. O nome e a descrição ajudarão a identificar o propósito deste fluxo.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleFormSubmit}>
                <div className="grid gap-4 py-4">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="name" className="text-left">
                      Nome
                    </Label>
                    <Input id="name" {...register('name')} />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="description" className="text-left">
                      Descrição
                    </Label>
                    <Textarea id="description" {...register('description')} />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit">Criar</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      ) : filteredFlows.length === 0 ? (
        <div className="text-center py-8 border rounded-lg bg-gray-50 dark:bg-gray-900">
          <h3 className="text-lg font-medium mb-1">Nenhum resultado encontrado</h3>
          <p className="text-sm text-gray-500">Tente ajustar os filtros ou termos de busca</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredFlows.map((flow) => (
            <FlowListItem
              key={flow.id}
              id={flow.id}
              name={flow.name}
              description={flow.description}
              status={flow.status}
              createdAt={flow.createdAt}
              updatedAt={flow.updatedAt}
              lastRun={flow.lastRun}
              executionCount={flow.runCount}
              messageCount={flow.sendMessageCount}
              conversionRate={flow.conversionRate}
              href={`/flows/${flow.id}/edit`}
              onDelete={handleDeleteFlow}
            />
          ))}
        </div>
      )}
    </div>
  )
}
